import React, { useEffect, useRef, useState } from 'react'
import { coneGradient } from './designPreview'

interface ViewConeProps {
  /** Where the camera is pointed, in compass degrees clockwise from north. */
  yaw: number
  /**
   * How much of the scene is on screen, left edge of the picture to right
   * edge, in degrees. The controller computes this from the zoom, so zooming
   * in narrows the cone and zooming out opens it.
   */
  fov: number
  /** The same, top edge of the picture to bottom. */
  fovVertical: number
  /**
   * The above or below view. The camera then looks straight down (or up)
   * and sees all the way round the viewer at once, so there is no slice to
   * draw: the cone opens round into a circle.
   */
  overhead: boolean
  /**
   * The square the shape is drawn in. Its centre is the viewer, and its
   * inscribed circle is the avatars' orbit.
   */
  size: number
}

// The cone never draws narrower than this, so it stays visible at any zoom.
const MIN_SPAN = 8
// What to draw when the camera has not reported a usable angle yet. A player
// laid out in a container with no height divides by zero working its aspect
// out, which arrives here as NaN and would otherwise collapse the shape.
const FALLBACK_SPAN = 90
// The smallest circle, as a fraction of the orbit, however far in the zoom goes.
const MIN_RADIUS = 0.15

// One polygon point per this many degrees along the curved end. Six is under
// a pixel of error on a 90px cone and keeps the list short enough to rebuild
// every frame without thinking about it.
const ARC_STEP = 6

// How long the cone takes to open into the circle, and to close back. About
// as long as the camera's own swing between the views.
const MORPH_MS = 600

// The facing marker on the circle: a white chevron just inside the rim.
const NOTCH_WIDTH = 0.1 // of the box
const NOTCH_INSET = 0.03 // of the box, between the chevron's tip and the rim

// How see-through the shape is at the viewer; solid at the orbit. As a mask,
// black is opaque and its alpha is what counts, so the ramp is alpha this
// to 1. `closest-side` puts 100% on the orbit, as the colour gradient does.
// Gavin, 30 Sep, on the 50% cut: "30% transparency in the centre".
const CENTRE_OPACITY = 0.7
const CONE_FADE = `radial-gradient(circle closest-side at 50% 50%, rgba(0, 0, 0, ${CENTRE_OPACITY}) 0%, rgba(0, 0, 0, 1) 100%)`

const clamp = (min: number, value: number, max: number): number =>
  Math.min(max, Math.max(min, value))

/**
 * The shape to draw, as a span in degrees and a radius as a fraction of the
 * orbit. `blend` runs 0 (the cone, normal view) to 1 (the circle, above or
 * below view); everything between is the one opening into the other.
 *
 * The CONE is Gavin's 16 Sep radar with Kai's curved end: a wedge whose
 * corners sit on the avatars' orbit at plus and minus half the viewing angle.
 *
 * The CIRCLE is how much of the whole scene is visible from above or below.
 * The camera then sits on the video sphere looking through its centre, and a
 * camera on a sphere looking at its centre sees, around the point opposite
 * it, a cap TWICE as wide as its own viewing angle (the inscribed angle
 * theorem). That cap runs from nothing to the entire sphere, 180° out, so the
 * circle is that share of the dial: full size only when every direction is
 * on screen at once. The narrower of the two screen angles, because a circle
 * has to fit across the screen both ways.
 *
 * Not the horizon, 90° out, where the faces sit: sizing to that (16 Sep, first
 * pass) filled the dial by zoom 0.85, and the above view spends most of its
 * range wider than that while the below view never gets narrower — so the
 * circle sat at full size and zooming did nothing to it.
 */
export const viewShape = (
  fov: number,
  fovVertical: number,
  blend: number,
): { span: number; radius: number } => {
  const coneSpan = Number.isFinite(fov) ? clamp(MIN_SPAN, fov, 360) : FALLBACK_SPAN
  const narrow = Math.min(fov, fovVertical)
  const circleRadius = Number.isFinite(narrow) ? clamp(MIN_RADIUS, narrow / 180, 1) : 1
  const t = clamp(0, blend, 1)
  return {
    span: coneSpan + (360 - coneSpan) * t,
    radius: 1 + (circleRadius - 1) * t,
  }
}

/**
 * The shape's outline as clip-path points, in percentages of its square box,
 * pointing straight up. Exported for the test that pins it to Gavin's mock.
 */
export const shapePoints = (span: number, radius: number): string[] => {
  const half = span / 2
  const point = (deg: number): string => {
    const rad = (deg * Math.PI) / 180
    return `${50 + 50 * radius * Math.sin(rad)}% ${50 - 50 * radius * Math.cos(rad)}%`
  }
  const steps = Math.max(1, Math.ceil(span / ARC_STEP))
  const rim = Array.from({ length: steps + 1 }, (_, i) => point(-half + (span * i) / steps))
  // A whole circle needs no apex; a wedge starts and ends at the viewer.
  return span >= 360 ? rim : ['50% 50%', ...rim]
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Eases toward `target` over `durationMs`. Interrupted midway, it turns round
// from wherever it had got to rather than jumping.
const useEased = (target: number, durationMs: number): number => {
  const [value, setValue] = useState(target)
  const current = useRef(target)
  useEffect(() => {
    const from = current.current
    if (from === target) return
    if (prefersReducedMotion()) {
      current.current = target
      setValue(target)
      return
    }
    const start = performance.now()
    let frame = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
      current.current = from + (target - from) * eased
      setValue(current.current)
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, durationMs])
  return value
}

const ViewCone: React.FC<ViewConeProps> = ({ yaw, fov, fovVertical, overhead, size }) => {
  const blend = useEased(overhead ? 1 : 0, MORPH_MS)
  const { span, radius } = viewShape(fov, fovVertical, blend)
  const notchWidth = size * NOTCH_WIDTH
  const notchHeight = notchWidth * 0.7

  return (
    // The wrapper turns the shape to the heading and carries its shadow. The
    // shadow has to live out here: an element's own clip-path cuts away its
    // own drop-shadow, so a filter on the clipped box draws nothing.
    <div
      aria-hidden="true"
      style={{
        position: 'relative',
        width: size,
        height: size,
        // Negative, because yaw grows anticlockwise while a CSS rotation turns
        // the other way — the same sign the radar discs are drawn with.
        transform: `rotate(${-yaw}deg)`,
        // Invisible over the black frames the mock was made on, and the only
        // thing holding the shape up over a bright sky now that Gavin has taken
        // the glass plate away.
        filter: 'drop-shadow(0 1px 4px rgba(0, 0, 0, 0.55))',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          width: size,
          height: size,
          // In colour, cyan at the viewer spreading out to green, in rings; in
          // mono (the default since 16 Oct), greys. `closest-side` makes the
          // gradient's 100% the orbit radius, which is what the stops are
          // measured against — so a shade is a distance, in the cone and the
          // circle alike. See designPreview.ts for both ramps.
          background: coneGradient(),
          // Gavin, 30 Sep: "gradual transparency from centre of radar disk -
          // no more than 50%", then on the first cut: "solid on outer and
          // transparent centre - radiating out from the centre". Half
          // see-through at the viewer (30%, his second note), solid at the
          // orbit, a straight ramp between. Measured against the orbit like the colour is, so
          // opacity is a distance too: the cone's far end always sits on the
          // orbit and is always solid, while a small overhead circle stays
          // mostly see-through.
          maskImage: CONE_FADE,
          WebkitMaskImage: CONE_FADE,
          clipPath: `polygon(${shapePoints(span, radius).join(', ')})`,
        }}
      />
      {/* Which way the viewer faces. A cone points by itself; a circle has no
          front, so the marker fades in as the cone opens round. Inside the rim
          rather than on it, because the rim at full size is the avatars' orbit
          and a face would sit on top of it. */}
      <div
        style={{
          position: 'absolute',
          left: size / 2 - notchWidth / 2,
          top: size / 2 - (size / 2) * radius + size * NOTCH_INSET,
          width: notchWidth,
          height: notchHeight,
          background: 'rgba(255, 255, 255, 0.92)',
          clipPath: 'polygon(50% 0%, 100% 100%, 50% 72%, 0% 100%)',
          opacity: blend,
        }}
      />
    </div>
  )
}

export default ViewCone
